<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import MultiPlaceGiftIdeasApi from '@consumer/api/MultiPlaceGiftIdeasApi'
import { useSharedData } from '@consumer/composables/sharedData'
import type { MultiplaceGift } from '@consumer/types/serializers'
import type Option from '@corp/types/serializers/Option'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
} from '@headlessui/vue'
import { useElementSize } from '@vueuse/core'
import { typeaheadDebounce } from '@shared/debounce'
import carouselUpIcon from '@consumer/assets/icons/carousel-up.svg?svgicon'
import carouselDownIcon from '@consumer/assets/icons/carousel-down.svg?svgicon'

// NOTE: Mirrors the server minimum — a shorter term can't use the username
// trigram index, so it would scan every user.
const MIN_SEARCH_LENGTH = 3

const giftlyUserOption = { value: null, label: 'Giftly' }
withDefaults(defineProps<{
  multiplaceGift: MultiplaceGift
  modelValue: number | null | undefined
  defaultOwner: Option
  includeGiftlyOption: boolean
}>(), {  })

const emit = defineEmits<{ 'update:modelValue': [value: any] }>()

const { currentUser } = useSharedData()

const options = _ref<Option[]>([
  __props.defaultOwner,
  ...(__props.includeGiftlyOption ? [giftlyUserOption] : []),
])
const query = _ref('')
let matchedUsers = _ref<Option[]>([])

const filteredOptions = _computed(() => {
  if (!query.value) return options.value
  const username = query.value.toLowerCase()
  const localMatches = options.value.filter(option => String(option.label).toLowerCase().includes(username))
  const localValues = new Set(localMatches.map(option => option.value))
  return [...localMatches, ...matchedUsers.value.filter(user => !localValues.has(user.value))]
})

const selectedOption = _computed<Option>({
  get () {
    return options.value.find(item => item.value === __props.modelValue) || __props.defaultOwner
  },
  set (option) {
    // A remotely fetched option has to be kept around, the getter resolves the
    // selected label from `options` and the search results are cleared on reset.
    if (option && !options.value.some(item => item.value === option.value)) options.value.push(option)

    emit('update:modelValue', option?.value)
  },
})

const comboboxRef = ref<HTMLElement>()
const { width } = useElementSize(comboboxRef)

const searchAttributionUsers = typeaheadDebounce(async (term: string) => {
  if (term.trim().length < MIN_SEARCH_LENGTH) {
    matchedUsers.value = []
    return
  }

  try {
    const users = await MultiPlaceGiftIdeasApi.attributionUsers<Option[]>({
      internalSlug: __props.multiplaceGift.internalSlug,
      query: { term },
      visit: false,
    })
    matchedUsers.value = users.filter(user => user.value !== __props.defaultOwner?.value)
  }
  catch (error) {
    console.error('Error fetching attribution users:', error)
    matchedUsers.value = []
  }
})

watch((query), () => {
  if (currentUser?.canEditGiftIdeas) searchAttributionUsers(query.value)
})
</script>
<template>
  <Combobox v-model="selectedOption" data-testid="user-combobox">
    <div class="flex flex-start items-center gap-4 mb-5">
      <GInputLabel label="Attribution"/>
      <div ref="comboboxRef" class="relative">
        <ComboboxInput
          :displayValue="(option: any) => option.label"
          class="g-input g-input-text g-input-small"
          @change="query = $event.target.value"
        />
        <ComboboxButton v-slot="{ open }" class="absolute inset-y-0 right-0 flex items-center p-3">
          <GIcon
            v-if="open"
            :svg="carouselUpIcon"
            size="12px"
            aria-hidden="true"
            color="grey-500"
            style="margin-top: -2px;"
          />
          <GIcon v-else :svg="carouselDownIcon" size="12px" aria-hidden="true" color="grey-500"/>
        </ComboboxButton>

        <ComboboxOptions
          class="mt-1 absolute w-full text-sm text-v2-secondary cursor-pointer select-none font-medium
                  bg-white rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-scroll
                  focus:outline-none border-1 border-solid border-grey-primary"
          :style="{ width: `${width}px`, maxHeight: `300px`, zIndex: 10,}"
        >
          <ComboboxOption
            v-for="option in filteredOptions"
            :key="option.value as any"
            v-slot="{ active }"
            :value="option"
          >
            <div
              class="py-2 px-4"
              :class="active && 'bg-surface-grey text-navy'"
              :data-testid="`combobox-option-${option.value}`"
            >
              {{ option.label }}
            </div>
          </ComboboxOption>
        </ComboboxOptions>
      </div>
    </div>
  </Combobox>
</template>
